import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiSearch, FiX, FiUpload, FiChevronDown, FiChevronRight, FiWifiOff } from 'react-icons/fi';
import { getFieldCodes, uploadFieldCodes } from '../services/api';
import { isMobileLayout } from '../utils/deviceLayout';

// Crew reference for the Carlson field-to-finish codes. The office uploads the
// .fld file; crews get code + description, searchable. The last good copy is kept
// in localStorage (no expiry) so the page still works with no signal.
const CACHE_KEY = 'fieldCodesCache.v1';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (e) {
    // storage full / blocked — page still works online
  }
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z');
  return d.toLocaleDateString();
}

function FieldCodes() {
  const mobile = isMobileLayout();
  const [data, setData] = useState(() => readCache());
  const [loading, setLoading] = useState(!readCache());
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getFieldCodes()
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        writeCache(res.data);
        setOffline(false);
        setError('');
      })
      .catch(() => {
        if (!alive) return;
        if (readCache()) setOffline(true);
        else setError("Couldn't load the field codes. Check your connection and try again.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const q = query.trim().toLowerCase();
  const categories = useMemo(() => {
    const cats = (data && data.categories) || [];
    if (!q) return cats;
    return cats
      .map((c) => ({
        ...c,
        codes: c.codes.filter(
          (r) => r.code.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.codes.length);
  }, [data, q]);

  const matchCount = categories.reduce((n, c) => n + c.codes.length, 0);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const res = await uploadFieldCodes(file);
      setData(res.data);
      writeCache(res.data);
      setOffline(false);
      setUploadMsg(`Uploaded ${res.data.library.name} — ${res.data.library.code_count} codes. Crews see it now.`);
    } catch (err) {
      setUploadMsg((err.response && err.response.data && err.response.data.error) || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const lib = data && data.library;
  const canUpload = data && data.can_upload;

  return (
    <div style={{ padding: mobile ? '16px 12px 90px' : '30px', maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: mobile ? '22px' : '28px', color: '#1f2937' }}>Field Codes</h1>
          {lib && (
            <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: 14 }}>
              {lib.name} · {lib.code_count} codes · updated {formatDate(lib.uploaded_at)}
            </p>
          )}
        </div>
        {canUpload && (
          <div>
            <input ref={fileRef} type="file" accept=".fld" onChange={handleUpload} style={{ display: 'none' }} />
            <button
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
                background: '#1f2f4f', color: '#fff', border: 'none', borderRadius: 8,
                fontWeight: 600, fontSize: 14, cursor: uploading ? 'wait' : 'pointer',
              }}
            >
              <FiUpload /> {uploading ? 'Uploading…' : lib ? 'Upload new version' : 'Upload .fld file'}
            </button>
          </div>
        )}
      </div>

      {uploadMsg && (
        <div style={{ background: '#eef2ff', color: '#1e3a8a', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>
          {uploadMsg}
        </div>
      )}

      {offline && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fef3c7', color: '#92400e', padding: '8px 12px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          <FiWifiOff /> No connection — showing the copy saved on this device.
        </div>
      )}

      {loading && <div style={{ color: '#6b7280', padding: 20 }}>Loading field codes…</div>}
      {error && !data && <div style={{ color: '#b91c1c', padding: 20 }}>{error}</div>}

      {!loading && data && !lib && (
        <div style={{ color: '#6b7280', padding: 20, background: '#f9fafb', borderRadius: 8 }}>
          No field code list has been uploaded yet.
          {canUpload && ' Use "Upload .fld file" to add the Carlson field-to-finish file.'}
        </div>
      )}

      {lib && (
        <>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or description…"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 38px 12px 36px',
                fontSize: 16, border: '1px solid #d1d5db', borderRadius: 10, outline: 'none',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                aria-label="Clear search"
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 8, cursor: 'pointer', color: '#6b7280' }}
              >
                <FiX />
              </button>
            )}
          </div>

          {q && (
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
              {matchCount} {matchCount === 1 ? 'match' : 'matches'}
            </div>
          )}

          {categories.map((cat) => {
            const isCollapsed = !q && collapsed[cat.category];
            return (
              <div key={cat.category} style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <button
                  onClick={() => setCollapsed((s) => ({ ...s, [cat.category]: !s[cat.category] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px',
                    background: '#eef2f7', border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontWeight: 700, fontSize: 15, color: '#1f2f4f',
                  }}
                >
                  {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
                  <span style={{ flex: 1 }}>{cat.category}</span>
                  <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>{cat.codes.length}</span>
                </button>
                {!isCollapsed && cat.codes.map((r, i) => (
                  <div
                    key={r.code + i}
                    style={{
                      display: 'flex', gap: 12, padding: '9px 14px', alignItems: 'baseline',
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <span style={{
                      fontFamily: 'Consolas, Menlo, monospace', fontWeight: 700, fontSize: 15,
                      color: '#111827', minWidth: mobile ? 96 : 130, flexShrink: 0, wordBreak: 'break-all',
                    }}>
                      {r.code}
                    </span>
                    <span style={{ fontSize: 15, color: '#374151' }}>{r.description}</span>
                  </div>
                ))}
              </div>
            );
          })}

          {q && matchCount === 0 && (
            <div style={{ color: '#6b7280', padding: 20 }}>No codes match "{query}".</div>
          )}
        </>
      )}
    </div>
  );
}

export default FieldCodes;
